/**
 * Kalkulator skor klinis Asesmen Pra Bedah (PAB 7) — FE preview.
 * Harus konsisten dengan App\Support\PraBedahScoreCalculator (backend
 * authoritative). FE hanya untuk preview live; nilai final dihitung ulang
 * di server saat saving.
 */

export type RiskLow = "low" | "intermediate" | "high";
export type CapriniKategori = "very_low" | "low" | "moderate" | "high";

/** Bobot faktor risiko Caprini RAM 2005. */
export const CAPRINI_BOBOT: Record<string, number> = {
  // 1 poin
  usia_41_60: 1,
  bedah_minor: 1,
  imt_gt_25: 1,
  edema_tungkai: 1,
  varises: 1,
  hamil_atau_nifas: 1,
  riwayat_abortus_berulang: 1,
  kontrasepsi_oral_atau_hrt: 1,
  sepsis_1bln: 1,
  penyakit_paru_serius_1bln: 1,
  fungsi_paru_abnormal_ppok: 1,
  infark_miokard_akut: 1,
  gagal_jantung_kongestif_1bln: 1,
  riwayat_ibd: 1,
  pasien_medis_tirah_baring: 1,
  // 2 poin
  usia_61_74: 2,
  bedah_artroskopi: 2,
  bedah_mayor_terbuka_gt_45m: 2,
  bedah_laparoskopi_gt_45m: 2,
  keganasan: 2,
  tirah_baring_gt_72jam: 2,
  imobilisasi_gips: 2,
  akses_vena_sentral: 2,
  // 3 poin
  usia_ge_75: 3,
  riwayat_vte: 3,
  riwayat_keluarga_vte: 3,
  faktor_v_leiden: 3,
  protrombin_20210a: 3,
  antikoagulan_lupus: 3,
  antibodi_antikardiolipin: 3,
  homosistein_serum_tinggi: 3,
  hit: 3,
  trombofilia_lain: 3,
  // 5 poin
  stroke_1bln: 5,
  artroplasti_elektif_mayor: 5,
  fraktur_panggul_pelvis_tungkai: 5,
  cedera_medula_spinalis_akut_1bln: 5,
  multiple_trauma_1bln: 5
};

export const RCRI_KOMPONEN = [
  "bedah_risiko_tinggi",
  "penyakit_jantung_iskemik",
  "riwayat_gagal_jantung",
  "riwayat_serebrovaskular",
  "dm_dengan_insulin",
  "kreatinin_lebih_2"
];

export const STOPBANG_KOMPONEN = [
  "snoring",
  "tiredness",
  "observed_apnea",
  "hipertensi",
  "imt_gt_35",
  "usia_gt_50",
  "lingkar_leher_gt_40",
  "jenis_kelamin_pria"
];

type BoolMap = Record<string, boolean> | null | undefined;

export function hitungImt(
  bb: number | null | undefined,
  tb: number | null | undefined
): number | null {
  if (bb == null || tb == null) return null;
  const w = Number(bb);
  const h = Number(tb);
  if (!w || !h || w <= 0 || h <= 0) return null;
  const m = h / 100;
  return Math.round((w / (m * m)) * 10) / 10;
}

function jumlahKomponen(input: BoolMap, komponen: string[]): number | null {
  if (!input || Object.keys(input).length === 0) return null;
  let skor = 0;
  komponen.forEach(k => {
    if (input[k]) skor++;
  });
  return skor;
}

export function rcriSkor(input: BoolMap): number | null {
  return jumlahKomponen(input, RCRI_KOMPONEN);
}

export function rcriRisiko(skor: number | null): RiskLow | null {
  if (skor == null) return null;
  if (skor <= 1) return "low";
  if (skor === 2) return "intermediate";
  return "high";
}

export function capriniSkor(input: BoolMap): number | null {
  if (!input || Object.keys(input).length === 0) return null;
  let skor = 0;
  Object.keys(CAPRINI_BOBOT).forEach(k => {
    if (input[k]) skor += CAPRINI_BOBOT[k];
  });
  return skor;
}

export function capriniKategori(skor: number | null): CapriniKategori | null {
  if (skor == null) return null;
  if (skor <= 0) return "very_low";
  if (skor <= 2) return "low";
  if (skor <= 4) return "moderate";
  return "high";
}

export function stopbangSkor(input: BoolMap): number | null {
  return jumlahKomponen(input, STOPBANG_KOMPONEN);
}

export function stopbangRisiko(skor: number | null): RiskLow | null {
  if (skor == null) return null;
  if (skor <= 2) return "low";
  if (skor <= 4) return "intermediate";
  return "high";
}

export function asaLabel(
  klasifikasi: string | null | undefined,
  emergency = false
): string | null {
  if (!klasifikasi) return null;
  return "ASA " + klasifikasi + (emergency ? " E" : "");
}

const RISK_LABEL: Record<string, string> = {
  very_low: "Sangat Rendah",
  low: "Rendah",
  intermediate: "Sedang",
  moderate: "Sedang",
  high: "Tinggi"
};

export function riskLabel(v: string | null): string {
  if (!v) return "-";
  return RISK_LABEL[v] || v;
}

export function riskColor(v: string | null): string {
  switch (v) {
    case "very_low":
    case "low":
      return "green";
    case "intermediate":
    case "moderate":
      return "orange";
    case "high":
      return "red";
    default:
      return "grey";
  }
}
